import { useCallback, useContext, useMemo, useState } from "react";
import { Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import { AppContext, ContactInfoErrors } from "../App";
import { wafFetch } from "../utils/awsWafIntegration";
import { usePostHog } from "@posthog/react";

const REDIRECT_DELAY = 5000;
const REDIRECT_URL = "https://integratedlistening.com/about/find-a-provider/";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);

const validateForm = (form: any): ContactInfoErrors => {
  const errors: ContactInfoErrors = {};

  if (!form.firstName?.trim()) errors.firstName = "First name is required";
  if (!form.lastName?.trim()) errors.lastName = "Last name is required";
  if (!form.email?.trim()) {
    errors.email = "Email is required";
  } else if (!validateEmail(form.email)) {
    errors.email = "Please enter a valid email address";
  }

  return errors;
};

export const NavigationButtons = () => {
  const {
    activeSteps,
    currentStep,
    form,
    handleNextStep,
    handlePrevStep,
    setIsSubmissionCreated,
    setSubmission,
    handleNewError,
    setContactInfoErrors,
  } = useContext(AppContext);
  const posthog = usePostHog();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const stepState = useMemo(
    () => ({
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === activeSteps.length - 1,
      isFormValueMissing: !activeSteps[currentStep].isCompleted(form),
    }),
    [currentStep, activeSteps, form]
  );

  const handleCreateSubmission = useCallback(async () => {
    const errors = validateForm(form);
    setContactInfoErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setIsSubmitting(true);
      const createSubmissionUrl = `${import.meta.env.VITE_UNYTE_WEBHOOKS_URL}/connectee-submissions`;
      const response = await wafFetch(createSubmissionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.error);
      }

      const createdSubmission = await response.json();
      if (!createdSubmission?.id) {
        throw new Error("Submission created but no id was returned");
      }

      posthog?.capture("provider_match_submitted", {
        for_whom: form.forWhom,
        age_group: form.providerAgeGroups,
        delivery_option: form.providerDeliveryOptions,
        country: form.country,
        state: form.state,
        specialties: form.providerClinicalSpecialties,
        submission_id: createdSubmission.id,
      });
      setSubmission(createdSubmission);
      setIsSubmissionCreated(true);
    } catch (error: unknown) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      posthog?.capture("submission_error", { error_message: errorMessage });
      handleNewError(`Failed to create the submission. Error: ${errorMessage}. Redirecting to integratedlistening.com`);

      if (import.meta.env.PROD) {
        setTimeout(() => (window.location.href = REDIRECT_URL), REDIRECT_DELAY);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    setContactInfoErrors,
    setIsSubmitting,
    setSubmission,
    setIsSubmissionCreated,
    handleNewError,
    posthog,
  ]);

  const handleClick = async () => {
    if (stepState.isLastStep) {
      await handleCreateSubmission();
    } else {
      posthog?.capture("form_step_advanced", {
        step_index: currentStep,
        step_label: activeSteps[currentStep].label,
      });
      handleNextStep();
    }
  };

  return (
    <Stack direction="row" justifyContent="center" p={2} spacing={12} sx={{ backgroundColor: "#EEF7F3" }}>
      <Button variant="outlined" disabled={stepState.isFirstStep} onClick={handlePrevStep}>
        Previous
      </Button>

      {!stepState.isLastStep && (
        <Button variant="contained" onClick={handleClick} disabled={stepState.isFormValueMissing}>
          Next
        </Button>
      )}

      {stepState.isLastStep && (
        <Tooltip
          title={
            stepState.isFormValueMissing && !isSubmitting
              ? "Please make sure that your name, email and captcha checkbox are filled"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={stepState.isFormValueMissing || isSubmitting}
              onClick={handleClick}
              startIcon={isSubmitting && <CircularProgress size={20} />}
            >
              FIND ME A PROVIDER
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};
